import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef } from 'react';
import { useAutoResize } from '@/hooks/useAutoResize';
import { useChatActions, useChatConfig, useChatState } from '@/ui/ChatProvider';
import { SendIcon, StopIcon } from '@/ui/primitives/Icon';
import { PoweredBy } from '@/ui/parts/PoweredBy';
export function Composer({ autoFocus }) {
    const { copy } = useChatConfig();
    const { send, setDraft, cancel, reset } = useChatActions();
    const draft = useChatState((s) => s.draft);
    const isSending = useChatState((s) => s.isSending);
    // A reply is on its way (the typing dots are up): wait for it before asking again.
    const awaiting = useChatState((s) => s.isAgentTyping && !s.isSending);
    const ready = useChatState((s) => s.status === 'ready');
    const closed = useChatState((s) => s.inactivity === 'closed');
    const textareaRef = useRef(null);
    useAutoResize(textareaRef, draft);
    // The box was disabled while the reply was on its way, which drops focus:
    // hand it back the moment they can write again.
    const wasAwaiting = useRef(false);
    useEffect(() => {
        if (wasAwaiting.current && !awaiting)
            textareaRef.current?.focus();
        wasAwaiting.current = awaiting;
    }, [awaiting]);
    const submit = (event) => {
        event?.preventDefault();
        if (!draft.trim() || isSending || awaiting)
            return;
        void send(draft);
    };
    // Enter sends; Shift+Enter inserts a newline. On touch keyboards Enter
    // should insert a newline instead, so we only intercept on pointer devices.
    const onKeyDown = (event) => {
        if (event.key !== 'Enter' || event.shiftKey)
            return;
        if (!window.matchMedia('(hover: hover)').matches)
            return;
        event.preventDefault();
        submit();
    };
    // Locked after inactivity: the input is replaced rather than disabled, so the
    // only thing left to do is the thing that works. Placed here rather than in
    // the notice because the transcript can be scrolled away from it.
    if (closed) {
        return (_jsxs("div", { className: "ac-composer", "data-locked": "true", children: [_jsx("button", { type: "button", className: "ac-restart", onClick: () => void reset(), children: copy.inactivityRestart }), _jsx(PoweredBy, {})] }));
    }
    return (_jsxs("form", { className: "ac-composer", onSubmit: submit, children: [_jsxs("div", { className: "ac-composer-row", children: [_jsx("label", { className: "ac-sr-only", htmlFor: "ac-input", children: copy.composerPlaceholder }), _jsx("textarea", { id: "ac-input", ref: textareaRef, rows: 1, value: draft, autoFocus: autoFocus, disabled: !ready || awaiting, placeholder: awaiting ? copy.awaitingReply : copy.composerPlaceholder, onChange: (e) => setDraft(e.target.value), onKeyDown: onKeyDown }), isSending ? (_jsx("button", { type: "button", className: "ac-send", "data-stop": "true", "aria-label": "Stop generating", onClick: cancel, children: _jsx(StopIcon, { size: 16 }) })) : (_jsx("button", { type: "submit", className: "ac-send", disabled: !draft.trim() || !ready || awaiting, "aria-label": copy.send, children: _jsx(SendIcon, { size: 16 }) }))] }), _jsx(PoweredBy, {})] }));
}
